import React, { createContext, useMemo, ReactElement, useContext, useEffect, useState } from 'react';
import { isSome } from 'fp-ts/lib/Option';
import { Loading, Result, ResultVariants } from '@dexma/ui-components';
import Profile from 'domain/entity/profile';
import { GetProfile } from 'use-case/profile/get/getProfileUseCase';
import { useCase, UseCaseState, UseCaseDispatch, CriteriaCallback } from 'adapter/in/common/hooks/useCase';
import i18n from 'asset/translation';

export type ProfileState = UseCaseState & { profile: Profile };

export const ProfileStateContext = createContext({} as ProfileState);
export const useProfile = (): ProfileState => useContext(ProfileStateContext);

type ProfileDispatch = UseCaseDispatch & { setProfile: (profile: Profile) => void; get: CriteriaCallback };

const ProfileDispatchContext = createContext({} as ProfileDispatch);
export const dispatchProfile = (): ProfileDispatch => useContext(ProfileDispatchContext);

type ProfileProvider = {
    getProfile: GetProfile;
    children: ReactElement;
};
export const ProfileStaticProvider = (props: ProfileProvider): ReactElement => {
    const { children, getProfile } = props;
    const [isLanguageLoaded, setIsLanguageLoaded] = useState(false);
    const { isLoading, data, error, setLoading, setData, setError, execute } = useCase(getProfile, false);
    const valueProfileDispatch = useMemo(
        () => ({
            setLoading,
            setProfile: setData,
            setError,
            get: execute,
        }),
        [setLoading, setData, setError]
    );

    useEffect(() => {
        execute();
    }, []);

    useEffect(() => {
        if (isSome(data)) {
            const profileValue = data.value;
            const { locale } = profileValue;
            i18n.changeLanguage(locale).then(() => setIsLanguageLoaded(true));
        }
    }, [data]);

    if (isSome(data) && isLanguageLoaded) {
        return (
            <ProfileStateContext.Provider value={{ isLoading, profile: data.value, error }}>
                <ProfileDispatchContext.Provider value={valueProfileDispatch}>{children}</ProfileDispatchContext.Provider>
            </ProfileStateContext.Provider>
        );
    }
    if (isSome(error)) {
        return (
            <Result
                variant={ResultVariants.ERROR}
                title={i18n.t('common.errorWrong')}
                info={i18n.t('common.errorWrongInfo')}
                icon='bot-dead'
            />
        );
    }
    return <Loading isLoading size={50} className='loading' />;
};
